import React from "react";
import { InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function lcmMany(numbers) {
  return numbers.reduce((acc, item) => lcm(acc, item), 1);
}

function normalizeFraction(numerator, denominator) {
  if (denominator < 0) return normalizeFraction(-numerator, -denominator);
  if (numerator === 0) return { numerator: 0, denominator: 1 };
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function randomProperFraction(denominator = randInt(3, 10)) {
  const candidates = Array.from({ length: denominator - 1 }, (_, index) => index + 1).filter(
    (numerator) => gcd(numerator, denominator) === 1
  );
  return { numerator: pick(candidates), denominator };
}

function multiplyFractions(a, b) {
  return normalizeFraction(a.numerator * b.numerator, a.denominator * b.denominator);
}

function addFractions(a, b) {
  return normalizeFraction(a.numerator * b.denominator + b.numerator * a.denominator, a.denominator * b.denominator);
}

function subtractFractions(a, b) {
  return normalizeFraction(a.numerator * b.denominator - b.numerator * a.denominator, a.denominator * b.denominator);
}

function fractionLatex(numerator, denominator) {
  if (denominator === 1) return `${numerator}`;
  if (numerator < 0) return `-\\frac{${Math.abs(numerator)}}{${denominator}}`;
  return `\\frac{${numerator}}{${denominator}}`;
}

function fractionValueLatex(fraction) {
  return fractionLatex(fraction.numerator, fraction.denominator);
}

function mixedLatex({ whole, numerator, denominator }) {
  if (!numerator) return `${whole}`;
  const sign = whole < 0 ? "-" : "";
  return `${sign}${Math.abs(whole)}\\frac{${numerator}}{${denominator}}`;
}

function fractionToMixed(fraction) {
  const sign = fraction.numerator < 0 ? -1 : 1;
  const absNumerator = Math.abs(fraction.numerator);
  const whole = Math.floor(absNumerator / fraction.denominator) * sign;
  const rest = absNumerator % fraction.denominator;
  const simpleRest = normalizeFraction(rest, fraction.denominator);
  return { whole, numerator: simpleRest.numerator, denominator: simpleRest.denominator };
}

function displayFractionLatex(fraction) {
  return fraction.display === "mixed" ? mixedLatex(fractionToMixed(fraction)) : fractionValueLatex(fraction);
}

function answerLatex(fraction) {
  if (Math.abs(fraction.numerator) > fraction.denominator && fraction.denominator !== 1) {
    return mixedLatex(fractionToMixed(fraction));
  }
  return fractionValueLatex(fraction);
}

function MathText({ math, size = "1.35rem" }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.15 }}>
      <InlineMath math={math} />
    </span>
  );
}

function makeMixedFraction(whole = randInt(1, 5), denominator = randInt(3, 9)) {
  const rest = randomProperFraction(denominator);
  return {
    numerator: whole * denominator + rest.numerator,
    denominator,
    display: "mixed",
  };
}

function expressionLatex(terms, useDisplay = true) {
  return terms
    .map((term, index) => {
      const body = useDisplay ? displayFractionLatex(term.fraction) : fractionValueLatex(term.fraction);
      if (index === 0) return body;
      if (term.op === "\\cdot") return `\\cdot ${body}`;
      return `${term.op}${body}`;
    })
    .join("");
}

function parseRawFraction(raw) {
  if (typeof raw !== "string") return null;
  const normalized = raw.trim().replace(",", ".").replace(/[−–]/g, "-");
  const mixed = normalized.replace(/\s+/g, " ").match(/^(-?\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const numerator = Number(mixed[2]);
    const denominator = Number(mixed[3]);
    if (!Number.isInteger(whole) || !Number.isInteger(numerator) || !Number.isInteger(denominator)) return null;
    if (denominator === 0 || numerator <= 0 || numerator >= denominator) return null;
    const sign = whole < 0 ? -1 : 1;
    return normalizeFraction(sign * (Math.abs(whole) * denominator + numerator), denominator);
  }
  const cleaned = normalized.replace(/\s+/g, "");
  const match = cleaned.match(/^(-?\d+)\/(-?\d+)$/);
  if (match) {
    const numerator = Number(match[1]);
    const denominator = Number(match[2]);
    if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator === 0) return null;
    return normalizeFraction(numerator, denominator);
  }
  if (/^-?\d+$/.test(cleaned)) return { numerator: Number(cleaned), denominator: 1 };
  return null;
}

function evaluateTerms(terms) {
  let currentProduct = terms[0].fraction;
  let result = null;
  let pendingSign = "+";

  for (let i = 1; i < terms.length; i += 1) {
    const term = terms[i];
    if (term.op === "\\cdot") {
      currentProduct = multiplyFractions(currentProduct, term.fraction);
    } else {
      result = result
        ? pendingSign === "+"
          ? addFractions(result, currentProduct)
          : subtractFractions(result, currentProduct)
        : pendingSign === "+"
        ? currentProduct
        : normalizeFraction(-currentProduct.numerator, currentProduct.denominator);
      pendingSign = term.op;
      currentProduct = term.fraction;
    }
  }

  return result
    ? pendingSign === "+"
      ? addFractions(result, currentProduct)
      : subtractFractions(result, currentProduct)
    : currentProduct;
}

function makeTask(type, terms, meta = {}) {
  return { type, data: { terms, result: evaluateTerms(terms), ...meta } };
}

function simpleMultiplicationTask() {
  return makeTask("FractionMultiplySimpleTask", [
    { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
    { op: "\\cdot", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function cancellableMultiplicationTask() {
  const pairs = [
    [{ numerator: 2, denominator: 9 }, { numerator: 3, denominator: 8 }],
    [{ numerator: 5, denominator: 12 }, { numerator: 6, denominator: 7 }],
    [{ numerator: 4, denominator: 15 }, { numerator: 5, denominator: 6 }],
    [{ numerator: 7, denominator: 10 }, { numerator: 5, denominator: 14 }],
    [{ numerator: 9, denominator: 16 }, { numerator: 8, denominator: 15 }],
  ];
  const [left, right] = pick(pairs);
  return makeTask("FractionMultiplyCancellableTask", [
    { op: "+", fraction: left },
    { op: "\\cdot", fraction: right },
  ]);
}

function improperMultiplicationTask() {
  const leftDenominator = randInt(3, 8);
  const rightDenominator = randInt(3, 8);
  const left = normalizeFraction(randInt(leftDenominator + 1, leftDenominator * 3 - 1), leftDenominator);
  const right = randomProperFraction(rightDenominator);
  return makeTask("FractionMultiplyImproperTask", [
    { op: "+", fraction: left },
    { op: "\\cdot", fraction: right },
  ]);
}

function multiplicationPlusTask() {
  return makeTask("FractionMultiplyPlusTask", [
    { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
    { op: "\\cdot", fraction: randomProperFraction(randInt(3, 9)) },
    { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function multiplicationMinusTask() {
  let terms;
  do {
    terms = [
      { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "-", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "\\cdot", fraction: randomProperFraction(randInt(3, 9)) },
    ];
  } while (evaluateTerms(terms).numerator <= 0);
  return makeTask("FractionMultiplyMinusTask", terms);
}

function mixedMultiplicationTask() {
  return makeTask("FractionMultiplyMixedTask", [
    { op: "+", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 8)) },
    { op: "\\cdot", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function mixedWithAdditionTask() {
  return makeTask("FractionMultiplyMixedPlusTask", [
    { op: "+", fraction: makeMixedFraction(randInt(1, 3), randInt(3, 8)) },
    { op: "\\cdot", fraction: randomProperFraction(randInt(3, 9)) },
    { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function mixedWithSubtractionTask() {
  let terms;
  do {
    terms = [
      { op: "+", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 8)) },
      { op: "\\cdot", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "-", fraction: randomProperFraction(randInt(3, 9)) },
    ];
  } while (evaluateTerms(terms).numerator <= 0);
  return makeTask("FractionMultiplyMixedMinusTask", terms);
}

export function generateTasks() {
  return [
    simpleMultiplicationTask(),
    cancellableMultiplicationTask(),
    improperMultiplicationTask(),
    multiplicationPlusTask(),
    multiplicationMinusTask(),
    mixedMultiplicationTask(),
    mixedWithAdditionTask(),
    mixedWithSubtractionTask(),
  ];
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <MathText math={expressionLatex(data.terms)} />.
    </p>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label="wynik" placeholder="np. 3/8" />;
}

export function checkAnswer({ data }, userAnswer) {
  const raw = parseRawFraction(userAnswer?.raw);
  if (!raw) return false;
  return raw.numerator === data.result.numerator && raw.denominator === data.result.denominator;
}

export function renderAnswer({ data }) {
  return <MathText math={answerLatex(data.result)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  const parsed = parseRawFraction(userAnswer.raw);
  return <MathText math={parsed ? answerLatex(parsed) : userAnswer.raw} />;
}

function mixedConversionLine(term) {
  if (term.fraction.display !== "mixed") return null;
  const mixed = fractionToMixed(term.fraction);
  return `${displayFractionLatex(term.fraction)}=\\frac{${mixed.whole}\\cdot ${mixed.denominator}+${mixed.numerator}}{${mixed.denominator}}=${fractionValueLatex(
    term.fraction
  )}`;
}

function multiplicationGroups(terms) {
  const groups = [];
  let current = [terms[0]];
  for (let i = 1; i < terms.length; i += 1) {
    if (terms[i].op === "\\cdot") current.push(terms[i]);
    else {
      groups.push(current);
      current = [terms[i]];
    }
  }
  groups.push(current);
  return groups.filter((group) => group.length > 1);
}

function reducedMultiplicationGroup(group) {
  const items = group.map((term) => ({
    numerator: Math.abs(term.fraction.numerator),
    denominator: term.fraction.denominator,
    reducedNumerator: Math.abs(term.fraction.numerator),
    reducedDenominator: term.fraction.denominator,
  }));

  for (let i = 0; i < items.length; i += 1) {
    for (let j = 0; j < items.length; j += 1) {
      const divisor = gcd(items[i].reducedNumerator, items[j].reducedDenominator);
      if (divisor > 1) {
        items[i].reducedNumerator /= divisor;
        items[j].reducedDenominator /= divisor;
      }
    }
  }

  return items;
}

function groupHasCancellation(items) {
  return items.some(
    (item) => item.numerator !== item.reducedNumerator || item.denominator !== item.reducedDenominator
  );
}

function groupProduct(group) {
  return group.slice(1).reduce((acc, term) => multiplyFractions(acc, term.fraction), group[0].fraction);
}

function reducedGroupProduct(items) {
  const fractions = items.map((item) => ({
    numerator: item.reducedNumerator,
    denominator: item.reducedDenominator,
  }));
  return fractions.slice(1).reduce((acc, fraction) => multiplyFractions(acc, fraction), fractions[0]);
}

function multiplicationLine(group) {
  const product = groupProduct(group);
  return `${expressionLatex(group, false)}=${fractionLatex(
    group.map((term) => term.fraction.numerator).join("\\cdot "),
    group.map((term) => term.fraction.denominator).join("\\cdot ")
  )}=${fractionValueLatex(product)}`;
}

function reducedMultiplicationLine(items) {
  return `${fractionLatex(
    items.map((item) => item.reducedNumerator).join("\\cdot "),
    items.map((item) => item.reducedDenominator).join("\\cdot ")
  )}=${fractionValueLatex(reducedGroupProduct(items))}`;
}

function productTerms(terms) {
  const products = [];
  let current = [terms[0]];
  for (let i = 1; i < terms.length; i += 1) {
    if (terms[i].op === "\\cdot") {
      current.push(terms[i]);
    } else {
      products.push({
        op: current[0].op,
        fraction: current.slice(1).reduce((acc, term) => multiplyFractions(acc, term.fraction), current[0].fraction),
      });
      current = [terms[i]];
    }
  }
  products.push({
    op: current[0].op,
    fraction: current.slice(1).reduce((acc, term) => multiplyFractions(acc, term.fraction), current[0].fraction),
  });
  return products;
}

function extensionLine(term, commonDenominator) {
  const factor = commonDenominator / term.fraction.denominator;
  return {
    original: term.fraction,
    factor,
    expanded: { numerator: term.fraction.numerator * factor, denominator: commonDenominator },
  };
}

function expandedExpressionLatex(terms, commonDenominator) {
  return terms
    .map((term, index) => {
      const expanded = extensionLine(term, commonDenominator).expanded;
      const body = fractionValueLatex(expanded);
      if (index === 0) return body;
      return `${term.op === "+" ? "+" : "-"}${body}`;
    })
    .join("");
}

function numeratorExpressionLatex(terms, commonDenominator) {
  return terms
    .map((term, index) => {
      const expanded = extensionLine(term, commonDenominator).expanded;
      const body = `${Math.abs(expanded.numerator)}`;
      if (index === 0) return `${expanded.numerator}`;
      const sign = term.op === "+" ? "+" : "-";
      return `${sign}${body}`;
    })
    .join("");
}

function CancelledNumber({ value, reduced, placement = "top" }) {
  const changed = value !== reduced;
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        minWidth: 24,
        overflow: "visible",
      }}
    >
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <InlineMath math={`${value}`} />
        {changed && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: -1,
              right: -1,
              top: "50%",
              borderTop: "3px solid #f59e0b",
              transform: "translateY(-50%)",
            }}
          />
        )}
      </span>
      {changed && (
        <span
          style={{
            position: "absolute",
            left: "calc(100% + 2px)",
            top: placement === "top" ? -13 : "auto",
            bottom: placement === "bottom" ? -13 : "auto",
            color: "#f59e0b",
            fontSize: "0.72em",
            lineHeight: 1,
          }}
        >
          <InlineMath math={`${reduced}`} />
        </span>
      )}
    </span>
  );
}

function CancellationFraction({ item }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2, fontSize: "1.2rem" }}>
      <CancelledNumber value={item.numerator} reduced={item.reducedNumerator} />
      <span style={{ width: "100%", minWidth: 34, borderTop: "2px solid currentColor" }} />
      <CancelledNumber value={item.denominator} reduced={item.reducedDenominator} placement="bottom" />
    </span>
  );
}

export function CancellationProduct({ group }) {
  const items = reducedMultiplicationGroup(group);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "12px 0" }}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span style={{ fontSize: "1.35rem" }}>·</span>}
          <CancellationFraction item={item} />
        </React.Fragment>
      ))}
      <span style={{ fontSize: "1.35rem" }}>=</span>
      <MathText math={reducedMultiplicationLine(items).split("=")[0]} />
      <span style={{ fontSize: "1.35rem" }}>=</span>
      <MathText math={fractionValueLatex(reducedGroupProduct(items))} />
    </div>
  );
}

function renderCombinedOperationsStep(terms, result, stepNumber) {
  const products = productTerms(terms);
  const commonDenominator = lcmMany(products.map((term) => term.fraction.denominator));
  const expandedTerms = products.map((term) => extensionLine(term, commonDenominator));
  const numeratorValue = result.numerator * (commonDenominator / result.denominator);
  const denominatorsAreCommon = products.every((term) => term.fraction.denominator === commonDenominator);

  return (
    <>
      <p>
        <strong>Krok {stepNumber}:</strong> Wstawiamy obliczone iloczyny do całego działania.
      </p>
      <p>
        <MathText math={`${expressionLatex(terms, false)}=${expressionLatex(products, false)}`} />
      </p>
      {denominatorsAreCommon ? (
        <p>Mianowniki są takie same, więc dodajemy albo odejmujemy liczniki.</p>
      ) : (
        <>
          <p>Sprowadzamy ułamki do wspólnego mianownika.</p>
          {expandedTerms.map((item, index) => (
            <p key={`expanded-${index}`}>
              <MathText
                math={`${fractionValueLatex(item.original)}=${fractionLatex(
                  `${item.original.numerator}\\cdot ${item.factor}`,
                  `${item.original.denominator}\\cdot ${item.factor}`
                )}=${fractionValueLatex(item.expanded)}`}
              />
            </p>
          ))}
        </>
      )}
      <p>
        <MathText
          math={`${expressionLatex(products, false)}=${expandedExpressionLatex(
            products,
            commonDenominator
          )}=\\frac{${numeratorExpressionLatex(products, commonDenominator)}}{${commonDenominator}}=\\frac{${numeratorValue}}{${commonDenominator}}`}
        />
      </p>
      {result.denominator !== commonDenominator && (
        <p>
          Skracamy wynik i otrzymujemy <MathText math={fractionValueLatex(result)} />.
        </p>
      )}
    </>
  );
}

function renderMultiplicationStep(group, index, stepNumber, showStepLabel) {
  const reducedItems = reducedMultiplicationGroup(group);
  const canReduce = groupHasCancellation(reducedItems);
  const label = showStepLabel ? <strong>Krok {stepNumber}:</strong> : null;

  return (
    <React.Fragment key={`group-${index}`}>
      {canReduce ? (
        <>
          <p>
            {label} Skracamy wspólne czynniki w licznikach i mianownikach przed mnożeniem.
          </p>
          <CancellationProduct group={group} />
        </>
      ) : (
        <>
          <p>
            {label} Mnożymy licznik przez licznik i mianownik przez mianownik.
          </p>
          <p>
            <MathText math={multiplicationLine(group)} />
          </p>
        </>
      )}
    </React.Fragment>
  );
}

export function renderExplanation({ data }) {
  const mixedConversions = data.terms.map(mixedConversionLine).filter(Boolean);
  const groups = multiplicationGroups(data.terms);
  const hasAdditionalOperations = data.terms.some((term, index) => index > 0 && term.op !== "\\cdot");
  const showStepLabels = mixedConversions.length > 0 || hasAdditionalOperations || groups.length > 1;
  let stepNumber = 1;
  return (
    <div>
      {mixedConversions.length > 0 && (
        <>
          <p>
            <strong>Krok {stepNumber++}:</strong> Najpierw zamieniamy liczby mieszane na ułamki niewłaściwe.
          </p>
          {mixedConversions.map((line, index) => (
            <p key={`mixed-${index}`}>
              <MathText math={line} />
            </p>
          ))}
        </>
      )}
      <p>
        Przy mnożeniu ułamków nie sprowadzamy ich do wspólnego mianownika. Licznik mnożymy przez licznik, a mianownik
        przez mianownik.
      </p>
      {groups.map((group, index) => renderMultiplicationStep(group, index, stepNumber++, showStepLabels))}
      {hasAdditionalOperations && (
        renderCombinedOperationsStep(data.terms, data.result, stepNumber++)
      )}
      {Math.abs(data.result.numerator) > data.result.denominator && data.result.denominator !== 1 && (
        <p>
          Ułamek niewłaściwy zapisujemy jako liczbę mieszaną:{" "}
          <MathText math={`${fractionValueLatex(data.result)}=${mixedLatex(fractionToMixed(data.result))}`} />.
        </p>
      )}
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  FractionMultiplySimpleTask: taskAdapter,
  FractionMultiplyCancellableTask: taskAdapter,
  FractionMultiplyImproperTask: taskAdapter,
  FractionMultiplyPlusTask: taskAdapter,
  FractionMultiplyMinusTask: taskAdapter,
  FractionMultiplyMixedTask: taskAdapter,
  FractionMultiplyMixedPlusTask: taskAdapter,
  FractionMultiplyMixedMinusTask: taskAdapter,
};
